import { VerticalFlexbox } from "@src/components/layout/Flexbox";
import { Label } from "@src/components/ui/label";
import { Textarea as ShadTextarea } from "@src/components/ui/textarea";
import { cn } from "@src/lib/utils";
import { type TextareaHTMLAttributes, forwardRef, useId } from "react";

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  hint?: string;
  error?: string;
  fillHeight?: boolean;
  fillWidth?: boolean;
  resize?: "none" | "vertical" | "horizontal" | "both";
}

/**
 * Labeled textarea built on the shadcn Textarea primitive. Supports the
 * legacy `fillHeight` prop used in the AI scribe note editor.
 */
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      id,
      className,
      label,
      hint,
      error,
      fillHeight,
      fillWidth = true,
      resize,
      style,
      ...props
    },
    ref,
  ) => {
    const reactId = useId();
    const textareaId = id ?? reactId;

    const fillStyle: React.CSSProperties = {
      ...(fillHeight
        ? {
            height: "100%",
            minHeight: 0,
            flex: 1,
            resize: resize ?? "none",
          }
        : resize
          ? { resize }
          : {}),
      ...style,
    };

    return (
      <VerticalFlexbox
        gap="xs"
        className={cn(fillWidth && "w-full", fillHeight && "min-h-0 flex-1")}
      >
        {label ? (
          <Label htmlFor={textareaId} className="text-sm text-foreground">
            {label}
          </Label>
        ) : null}
        <ShadTextarea
          id={textareaId}
          ref={ref}
          aria-invalid={error ? "true" : undefined}
          className={cn(
            fillHeight && "h-full min-h-0 flex-1",
            error && "border-destructive ring-destructive",
            className,
          )}
          style={fillStyle}
          {...props}
        />
        {error ? (
          <span className="text-xs text-destructive">{error}</span>
        ) : hint ? (
          <span className="text-xs text-muted-foreground">{hint}</span>
        ) : null}
      </VerticalFlexbox>
    );
  },
);
Textarea.displayName = "Textarea";
